import { Fragment, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";

interface ErrorDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    errorMessage: string;
}

export const ErrorDialog: React.FC<ErrorDialogProps> = ({
    open,
    setOpen,
    errorMessage,
}) => {
    const cancelButtonRef = useRef(null);

    const onClose = () => {
        setOpen(false);
    };

    return (
        <Transition show={open} as={Fragment}>
            <Dialog
                className="relative z-[100]"
                initialFocus={cancelButtonRef}
                onClose={onClose}
            >
                {/* Overlay */}
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-eq-grey bg-opacity-50 transition-opacity" />
                </Transition.Child>

                {/* Dialog Panel Container */}
                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-sm bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start gap-4">
                                        {/* Error Icon */}
                                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                                            <span className="material-symbols-outlined text-[1.5rem] text-eq-green">
                                                error
                                            </span>
                                        </div>
                                        {/* Content */}
                                        <div className="w-full text-left">
                                            <Dialog.Title
                                                as="h3"
                                                className="text-base font-semibold leading-6 text-gray-900"
                                            >
                                                An Error Occurred
                                            </Dialog.Title>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500 whitespace-pre-wrap">
                                                    {errorMessage}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-row gap-4 px-4 pb-6 sm:flex-row-reverse sm:px-6">
                                    <button
                                        className="eq-secondary-button m-0 min-w-[115px]"
                                        onClick={onClose}
                                        ref={cancelButtonRef}
                                    >
                                        Close
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};